import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Clock, DollarSign, Calendar, Phone, FileSearch, Wrench } from 'lucide-react';
import { BarChart, Bar, Cell, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Task } from '../types';
import Confetti from 'react-confetti';
interface ResultsProps {
  tasks: Task[];
}
export function Results({
  tasks
}: ResultsProps) {
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0
  });
  useEffect(() => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);
  // Calculate scores and sort
  const scoredTasks = tasks.filter(t => t.revenueImpact !== null && t.repeatability !== null && t.timeSpent > 0).map(t => ({
    ...t,
    score: (t.revenueImpact || 0) * (t.repeatability || 0) * t.timeSpent
  })).sort((a, b) => b.score - a.score);
  const top3 = scoredTasks.slice(0, 3);
  const top3Hours = top3.reduce((sum, t) => sum + t.timeSpent, 0);
  const totalCurrentHours = scoredTasks.reduce((sum, t) => sum + t.timeSpent, 0);
  // Assumptions
  const automationReduction = 0.8; // 80% time saved
  const hourlyRate = 85;
  const hoursSavedPerWeek = top3Hours * automationReduction;
  const hoursSavedPerYear = hoursSavedPerWeek * 52;
  const costRecoveredPerYear = hoursSavedPerYear * hourlyRate;
  const chartData = [{
    name: 'Time spent now',
    hours: totalCurrentHours,
    fill: '#FFE5D4'
  }, {
    name: 'Time saved with AI',
    hours: Math.round(hoursSavedPerWeek),
    fill: '#7C5CFF'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  const medals = ['🥇', '🥈', '🥉'];
  return <div className="min-h-screen bg-sky/30 py-16 px-6" data-id="element-115">
      {showConfetti && <Confetti width={windowSize.width} height={windowSize.height} recycle={false} numberOfPieces={400} data-id="element-116" />}

      <motion.div className="max-w-5xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-117">
        {/* Header Stat */}
        <motion.div variants={itemVariants} className="text-center mb-16" data-id="element-118">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-sunshine text-ink rounded-full mb-6 shadow-soft-sm" data-id="element-119">
            <Trophy size={40} data-id="element-120" />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-ink mb-4 tracking-tight" data-id="element-121">
            You could automate{' '}
            <span className="text-grape" data-id="element-122">
              {Math.round(hoursSavedPerWeek)} hours/week
            </span>
          </h1>
          <p className="text-xl text-ink/70 font-medium max-w-2xl mx-auto" data-id="element-123">
            Based on your scores, here is your prioritized roadmap for AI
            automation. Start with the top 3 to get the highest return on your
            effort.
          </p>
        </motion.div>

        {/* Leaderboard */}
        <motion.div variants={itemVariants} className="bg-white rounded-[2.5rem] p-8 shadow-soft-lg mb-12 border-4 border-white" data-id="element-124">
          <h2 className="text-2xl font-bold text-ink mb-3 flex items-center gap-2" data-id="element-125">
            <Trophy className="text-sunshine" data-id="element-126" /> The Leaderboard
          </h2>
          <div className="bg-peach/50 rounded-2xl px-5 py-4 mb-6 text-ink/80 font-medium text-sm md:text-base" data-id="element-127">
            Ranked by{' '}
            <span className="inline-block bg-white text-coral font-extrabold px-2.5 py-1 rounded-lg font-mono text-xs md:text-sm whitespace-nowrap" data-id="element-128">
              Priority Score = (Revenue × 2) × Time × Repeatability
            </span>{' '}
            — so the tasks that move pipeline rise to the top, not just the time
            sinks.
          </div>
          <div className="overflow-x-auto" data-id="element-129">
            <table className="w-full text-left border-collapse" data-id="element-130">
              <thead data-id="element-131">
                <tr className="border-b-2 border-ink/5 text-ink/50 font-bold uppercase text-xs tracking-wider" data-id="element-132">
                  <th className="pb-4 pl-4" data-id="element-133">Rank</th>
                  <th className="pb-4" data-id="element-134">Task</th>
                  <th className="pb-4" data-id="element-135">Score</th>
                  <th className="pb-4" data-id="element-136">Hours/Wk</th>
                  <th className="pb-4" data-id="element-137">Impact</th>
                  <th className="pb-4" data-id="element-138">Repeatable</th>
                </tr>
              </thead>
              <tbody data-id="element-139">
                {scoredTasks.map((task, index) => {
                const isTop3 = index < 3;
                return <tr key={task.id} className={`border-b border-ink/5 last:border-0 ${isTop3 ? 'bg-butter/70' : ''}`} data-id="element-140">
                      <td className={`py-4 pl-4 font-bold text-lg ${isTop3 ? 'border-l-4 border-sunshine' : ''}`} data-id="element-141">
                        {isTop3 ? <span className="text-2xl" data-id="element-142">{medals[index]}</span> : <span className="text-ink/40 pl-2" data-id="element-143">{index + 1}</span>}
                      </td>
                      <td className={`py-4 font-bold ${isTop3 ? 'text-ink text-lg' : 'text-ink/80'}`} data-id="element-144">
                        {task.name}
                      </td>
                      <td className="py-4 font-black text-grape" data-id="element-145">
                        {Math.round(task.score)}
                      </td>
                      <td className="py-4 font-semibold text-ink/70" data-id="element-146">
                        {task.timeSpent}h
                      </td>
                      <td className="py-4 font-semibold text-ink/70" data-id="element-147">
                        {task.revenueImpact}/5
                      </td>
                      <td className="py-4 font-semibold text-ink/70" data-id="element-148">
                        {task.repeatability}/5
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Deloitte Insight Quote */}
        <motion.div variants={itemVariants} className="bg-ink text-white rounded-[2.5rem] p-8 md:p-10 mb-12 shadow-soft-lg flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10" data-id="element-149">
          <div className="flex-shrink-0" data-id="element-150">
            <div className="font-serif font-bold text-7xl md:text-8xl leading-none text-peach" data-id="element-151">
              84<span className="text-5xl md:text-6xl align-top" data-id="element-152">%</span>
            </div>
          </div>
          <div className="flex-1 md:border-l-2 md:border-white/10 md:pl-10" data-id="element-153">
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3 text-white" data-id="element-154">
              The gap is widening
            </h3>
            <p className="text-white/80 font-medium text-base md:text-lg leading-relaxed mb-3" data-id="element-155">
              of organizations raised AI investment this year. Automating these
              tasks keeps you ahead — without new headcount.
            </p>
            <p className="text-white/50 text-sm font-medium italic" data-id="element-156">
              Source: Deloitte State of AI in the Enterprise, January 2026.
            </p>
          </div>
        </motion.div>

        {/* Chart & Metrics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-157">
          <motion.div variants={itemVariants} className="bg-white rounded-[2.5rem] p-8 shadow-soft-lg border-4 border-white flex flex-col" data-id="element-158">
            <h2 className="text-2xl font-bold text-ink mb-2 flex items-center gap-2" data-id="element-159">
              <Clock className="text-coral" data-id="element-160" />
              Reclaim{' '}
              <span className="text-grape" data-id="element-161">
                {totalCurrentHours > 0 ? Math.round(hoursSavedPerWeek / totalCurrentHours * 100) : 0}
                %
              </span>{' '}
              of your week
            </h2>
            <p className="text-ink/60 font-medium mb-6" data-id="element-162">
              Your current total weekly hours vs hours you'd reclaim by
              automating your top 3.
            </p>
            <div className="flex-1 min-h-[300px]" data-id="element-163">
              <ResponsiveContainer width="100%" height="100%" data-id="element-164">
                <BarChart data={chartData} margin={{
                top: 30,
                right: 20,
                left: 0,
                bottom: 5
              }} barCategoryGap="15%" data-id="element-165">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1F1B2E10" data-id="element-166" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fill: '#1F1B2E',
                  fontWeight: 700,
                  fontSize: 13
                }} data-id="element-167" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#1F1B2E80',
                  fontWeight: 600
                }} label={{
                  value: 'hours / week',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: '#1F1B2E60',
                    fontWeight: 700,
                    fontSize: 12
                  }
                }} data-id="element-168" />
                  <Tooltip cursor={{
                  fill: '#1F1B2E05'
                }} contentStyle={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 0 rgba(31, 27, 46, 0.08)',
                  fontWeight: 'bold'
                }} formatter={(value: number) => [`${value} hrs`, 'Hours / week']} data-id="element-169" />
                  <Bar dataKey="hours" radius={[14, 14, 0, 0]} maxBarSize={120} isAnimationActive={true} data-id="element-170">
                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-171" />)}
                    <LabelList dataKey="hours" position="top" formatter={(value: number) => `${value}h`} style={{
                    fill: '#1F1B2E',
                    fontWeight: 800,
                    fontSize: 18
                  }} data-id="element-172" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <div className="grid grid-rows-2 gap-8" data-id="element-173">
            <motion.div variants={itemVariants} className="bg-mint rounded-[2.5rem] p-8 shadow-soft-lg border-4 border-white flex flex-col justify-center relative overflow-hidden" data-id="element-174">
              <Calendar className="absolute -right-4 -bottom-4 w-32 h-32 text-leaf/20" data-id="element-175" />
              <h3 className="text-ink/70 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-176">
                Hours Saved Per Year
              </h3>
              <div className="text-5xl font-black text-ink" data-id="element-177">
                {Math.round(hoursSavedPerYear).toLocaleString()}
              </div>
              <p className="text-ink/60 font-semibold mt-2" data-id="element-178">
                Assuming 80% automation on top 3 tasks
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-blush rounded-[2.5rem] p-8 shadow-soft-lg border-4 border-white flex flex-col justify-center relative overflow-hidden" data-id="element-179">
              <DollarSign className="absolute -right-4 -bottom-4 w-32 h-32 text-coral/20" data-id="element-180" />
              <h3 className="text-ink/70 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-181">
                Cost Recovered Per Year
              </h3>
              <div className="text-5xl font-black text-ink" data-id="element-182">
                ${Math.round(costRecoveredPerYear).toLocaleString()}
              </div>
              <p className="text-ink/60 font-semibold mt-2" data-id="element-183">
                Based on $85/hr loaded rate
              </p>
            </motion.div>
          </div>
        </div>

        {/* CTAs */}
        <motion.div variants={itemVariants} data-id="element-184">
          <h2 className="text-3xl font-extrabold text-center text-ink mb-8" data-id="element-185">
            Ready to start automating?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-186">
            <div className="bg-white rounded-[2rem] p-6 shadow-soft text-center flex flex-col items-center" data-id="element-187">
              <div className="w-12 h-12 bg-grape/10 text-grape rounded-full flex items-center justify-center mb-4" data-id="element-188">
                <Phone size={24} data-id="element-189" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-2" data-id="element-190">
                Free Strategy Call
              </h3>
              <p className="text-ink/60 font-medium mb-6 text-sm" data-id="element-191">
                Discuss your results with an AI expert and map out a plan.
              </p>
              <a href="https://meetings-eu1.hubspot.com/dben-simon" target="_blank" rel="noopener noreferrer" className="mt-auto w-full py-3 rounded-full bg-grape text-white font-bold hover:bg-grape/90 transition-colors text-center" data-id="element-192">
                Book Call
              </a>
            </div>

            <div className="bg-white rounded-[2rem] p-6 shadow-soft text-center flex flex-col items-center" data-id="element-193">
              <div className="w-12 h-12 bg-coral/10 text-coral rounded-full flex items-center justify-center mb-4" data-id="element-194">
                <FileSearch size={24} data-id="element-195" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-2" data-id="element-196">
                Custom AI Audit
              </h3>
              <p className="text-ink/60 font-medium mb-6 text-sm" data-id="element-197">
                Deep dive into your entire team's workflow to find hidden ROI.
              </p>
              <a href="https://meetings-eu1.hubspot.com/dben-simon" target="_blank" rel="noopener noreferrer" className="mt-auto w-full py-3 rounded-full bg-coral text-white font-bold hover:bg-coral/90 transition-colors text-center" data-id="element-198">
                Get Audit
              </a>
            </div>

            <div className="bg-white rounded-[2rem] p-6 shadow-soft text-center flex flex-col items-center" data-id="element-199">
              <div className="w-12 h-12 bg-leaf/10 text-leaf rounded-full flex items-center justify-center mb-4" data-id="element-200">
                <Wrench size={24} data-id="element-201" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-2" data-id="element-202">
                Build Automation
              </h3>
              <p className="text-ink/60 font-medium mb-6 text-sm" data-id="element-203">
                Hire our team to build and deploy your top 3 automations.
              </p>
              <a href="https://meetings-eu1.hubspot.com/dben-simon" target="_blank" rel="noopener noreferrer" className="mt-auto w-full py-3 rounded-full bg-leaf text-white font-bold hover:bg-leaf/90 transition-colors text-center" data-id="element-204">
                Get Quote
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}